import React from 'react';

// Imágenes tomadas del catálogo (misma carpeta de FollajeTabs): un modelo representativo por categoría.
// Para cambiar la referencia de una categoría basta con editar la ruta `imagen` de su fila.
// `catalogoId` es el id de la categoría en FollajeTabs (pestaña a la que lleva el botón).
// `descripcion` es un texto corto tomado de la descripción de la categoría en el catálogo,
// depurado para NO repetir lo que ya muestran las columnas Uso / Material / Formato.
const RUTA_IMAGENES = `${process.env.PUBLIC_URL}/assets/img/productos/follaje-artificial`;

const especificaciones = [
    {
        categoria: 'Follaje 3D',
        descripcion: 'Alto relieve y apariencia natural',
        uso: 'Interior y exterior',
        material: 'HDPE',
        formato: 'Panel',
        catalogoId: 'follaje-3d',
        imagen: 'follaje-3d/follaje-3d-cipressus.png'
    },
    {
        categoria: 'Follaje Premium',
        descripcion: 'La mayor variedad de diseños de la línea',
        uso: 'Interior y exterior',
        material: 'HDPE',
        formato: 'Panel y rollo',
        catalogoId: 'follaje-premium',
        imagen: 'follaje-premium/premium-fantasy.jpg'
    },
    {
        categoria: 'Follaje Estándar',
        descripcion: 'Presupuesto moderado, aspecto natural',
        uso: 'Interior',
        material: 'LDPE',
        formato: 'Panel',
        catalogoId: 'follaje-estandar',
        imagen: 'follaje-estandar/estandar-arrayan-verde-oscuro.jpg'
    },
    {
        categoria: 'Panel de Flores',
        descripcion: 'Paneles y rollos florales decorativos',
        uso: 'Interior',
        material: 'Poliéster',
        formato: 'Panel y rollo',
        catalogoId: 'panel-flores',
        imagen: 'panel-flores/flores/flores-palo-de-rosa-moneda.jpg'
    },
    {
        categoria: 'Expandible (Madera y Poliéster)',
        descripcion: 'Paneles tipo acordeón de instalación rápida',
        uso: 'Interior',
        material: 'Madera y poliéster',
        formato: 'Plegable',
        catalogoId: 'expandibles',
        imagen: 'expandibles/expandible-hiedra-2-tonos.png'
    },
    {
        categoria: 'Expandible (Madera y HDPE)',
        descripcion: 'Paneles tipo acordeón de instalación rápida',
        uso: 'Interior y exterior',
        material: 'Madera y HDPE',
        formato: 'Plegable',
        catalogoId: 'expandibles',
        imagen: 'expandibles/expandible-laurel-rojo.png'
    },
    {
        categoria: 'Lona',
        descripcion: 'Lona decorativa con estampado de follaje',
        uso: 'Interior',
        material: 'Nylon y poliéster',
        formato: 'Lona',
        catalogoId: 'lona',
        imagen: 'lona/lona-follaje.jpg'
    }
];

// Una sola estructura para todos los tamaños:
// - móvil (<576px): tarjeta por categoría, datos en 2 columnas, etiqueta sobre cada dato
// - tablet (576–991px): tarjeta por categoría, datos en una fila de 4
// - escritorio (≥992px): tabla con encabezado de columnas; las etiquetas por celda se ocultan
const ESTILOS = `
.esp-fol { --esp-borde: #e5e9ee; --esp-muted: #6c757d; }
.esp-fol__head { display: none; }
.esp-fol__row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 12px;
    align-items: end;
    padding: 20px 0;
    border-bottom: 1px solid var(--esp-borde);
}
.esp-fol__row:last-child { border-bottom: 0; }
.esp-fol__cat { grid-column: 1 / -1; display: flex; align-items: center; gap: 16px; min-width: 0; }
.esp-fol__cat-texto { min-width: 0; }
.esp-fol__cat-desc {
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.35;
    color: var(--esp-muted);
}
.esp-fol__thumb {
    flex: 0 0 auto;
    width: 84px;
    height: 63px;
    border-radius: 8px;
    object-fit: cover;
    background: #eef1ee;
    outline: 1px solid rgba(0, 0, 0, 0.07);
    outline-offset: -1px;
}
.esp-fol__cell::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 2px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--esp-muted);
}
.esp-fol__valor { display: flex; align-items: center; gap: 6px; font-size: 15px; line-height: 1.35; color: #343a40; }
.esp-fol__icono { display: inline-flex; flex: 0 0 auto; line-height: 0; }
.esp-fol__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-height: 44px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
}
.esp-fol__th { font-size: 12px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: var(--esp-muted); }

@media (min-width: 576px) {
    .esp-fol__row { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

@media (min-width: 992px) {
    .esp-fol__head,
    .esp-fol__row {
        display: grid;
        grid-template-columns: minmax(260px, 2.2fr) minmax(150px, 1.3fr) minmax(130px, 1fr) minmax(100px, 0.8fr) 150px;
        column-gap: 20px;
        align-items: center;
    }
    .esp-fol__head { padding: 16px 0 12px; border-bottom: 1px solid var(--esp-borde); }
    .esp-fol__row { padding: 14px 0; }
    .esp-fol__cat { grid-column: auto; }
    .esp-fol__cell::before { display: none; }
    .esp-fol__thumb { width: 96px; height: 72px; }
}
`;

const IconoSol = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </svg>
);

const IconoCasa = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11l9-7 9 7" />
        <path d="M5 10v10h14V10" />
    </svg>
);

const IconoFlecha = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

// Lleva al catálogo y le pide a FollajeTabs que abra la pestaña de la categoría.
// FollajeTabs escucha el evento 'follaje:categoria'; si aún no está montado (p. ej. carga diferida),
// lee `window.__follajeCategoria` al montarse.
const irAlCatalogo = (e, catalogoId) => {
    e.preventDefault();
    window.__follajeCategoria = catalogoId;
    window.dispatchEvent(new CustomEvent('follaje:categoria', { detail: catalogoId }));

    const destino = document.getElementById('catalogo');
    if (destino) {
        const reducir = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        destino.scrollIntoView({ behavior: reducir ? 'auto' : 'smooth', block: 'start' });
    }
};

const EspecificacionesFollaje = () => {
    return (
        <section id="especificaciones" className="bg-light py-1-9 py-lg-2-9">
            <style>{ESTILOS}</style>
            <div className="container">
                <div className="row justify-content-center mb-1-6">
                    <div className="col-lg-10 text-center">
                        <div className="title-style2 mb-1 wow fadeIn" data-wow-delay="100ms">
                            <h2 className="h2">Follaje Artificial para interiores y exteriores</h2>
                        </div>
                        <p className="mb-0 text-secondary text-sm">
                            Conoce los diferentes tipos de follaje artificial y su uso recomendado
                        </p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="bg-white rounded shadow-sm px-1-6 py-1">
                            <div className="esp-fol" role="table" aria-label="Especificaciones por categoría de follaje">
                                <div role="rowgroup">
                                    <div className="esp-fol__head" role="row">
                                        <div className="esp-fol__th" role="columnheader">Categoría</div>
                                        <div className="esp-fol__th" role="columnheader">Uso recomendado</div>
                                        <div className="esp-fol__th" role="columnheader">Material</div>
                                        <div className="esp-fol__th" role="columnheader">Formato</div>
                                        <div className="esp-fol__th" role="columnheader"><span className="visually-hidden">Ver en el catálogo</span></div>
                                    </div>
                                </div>

                                <div role="rowgroup">
                                    {especificaciones.map((fila) => (
                                        <div className="esp-fol__row" role="row" key={fila.categoria}>
                                            <div className="esp-fol__cat" role="rowheader">
                                                {/* Referencia visual: recorte 4:3 de un modelo del catálogo (decorativa, el nombre va al lado) */}
                                                <img
                                                    className="esp-fol__thumb"
                                                    src={`${RUTA_IMAGENES}/${fila.imagen}`}
                                                    alt=""
                                                    width="96"
                                                    height="72"
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                                <div className="esp-fol__cat-texto">
                                                    <h3 className="h6 mb-0 font-weight-700" style={{ fontSize: '16px' }}>{fila.categoria}</h3>
                                                    {fila.descripcion && (
                                                        <p className="esp-fol__cat-desc mb-0">{fila.descripcion}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="esp-fol__cell" role="cell" data-label="Uso recomendado">
                                                <span className="esp-fol__valor">
                                                    <span className="esp-fol__icono text-primary">
                                                        {fila.uso.includes('exterior') ? <IconoSol /> : <IconoCasa />}
                                                    </span>
                                                    {fila.uso}
                                                </span>
                                            </div>

                                            <div className="esp-fol__cell" role="cell" data-label="Material">
                                                <span className="esp-fol__valor">{fila.material}</span>
                                            </div>

                                            <div className="esp-fol__cell" role="cell" data-label="Formato">
                                                <span className="esp-fol__valor">{fila.formato}</span>
                                            </div>

                                            <div role="cell">
                                                <a
                                                    href="#catalogo"
                                                    className="btn btn-outline-primary rounded-pill esp-fol__btn"
                                                    onClick={(e) => irAlCatalogo(e, fila.catalogoId)}
                                                    aria-label={`Ver modelos de ${fila.categoria} en el catálogo`}
                                                >
                                                    Ver modelos
                                                    <IconoFlecha />
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EspecificacionesFollaje;